import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link } from 'react-router-dom';
import { Heart, MessageCircle, PlayCircle, X } from 'lucide-react';
import BackButton from '../components/BackButton';
import { IgLine } from '../components/PayFirstProof';
import testimonialShots from '../data/testimonials.json';
import proofVideos from '../data/proofVideos.json';
import { useT } from '../i18n';

// /se — every piece of real social evidence in one place: customer videos and
// the WhatsApp screenshots (numbers blurred). A link to send a hesitant buyer.
// To add more: screenshots via marketing/testimonials-raw/ +
// server/scripts/buildTestimonials.js; videos by uploading to
// gs://anabatal-assets/marketing/ and appending to data/proofVideos.json.
export default function SocialEvidence() {
  const t = useT();
  const [shotIndex, setShotIndex] = useState(null);

  useEffect(() => { window.scrollTo(0, 0); }, []);

  useEffect(() => {
    if (shotIndex === null) return undefined;
    const onKey = (e) => { if (e.key === 'Escape') setShotIndex(null); };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [shotIndex]);

  return (
    <div className="article-page se-page">
      <BackButton fallback="/" />

      <header className="article-hero">
        <span className="article-hero-icon"><Heart size={30} /></span>
        <h1 className="article-title">{t.seTitle}</h1>
        <p className="article-lede">{t.seLede}</p>
      </header>

      {proofVideos.length > 0 && (
        <section className="article-section">
          <h2 className="article-heading se-heading"><PlayCircle size={22} /> {t.seVideosTitle}</h2>
          <div className="se-videos">
            {proofVideos.map((v) => (
              <video
                key={v.src}
                className="se-video"
                src={v.src}
                poster={v.poster}
                controls
                playsInline
                preload="metadata"
              />
            ))}
          </div>
        </section>
      )}

      {testimonialShots.length > 0 && (
        <section className="article-section">
          <h2 className="article-heading se-heading"><MessageCircle size={22} /> {t.waProofTitle}</h2>
          <p className="wa-proof-subtitle">{t.waProofSubtitle}</p>
          <div className="se-shots">
            {testimonialShots.map((shot, i) => (
              <button
                key={shot.src}
                type="button"
                className="se-shot"
                onClick={() => setShotIndex(i)}
                aria-label={t.waProofView}
              >
                <img src={shot.src} alt={t.waProofAlt} loading="lazy" width={shot.w} height={shot.h} />
              </button>
            ))}
          </div>
        </section>
      )}

      <IgLine t={t} />

      <div className="article-cta">
        <Link to="/" className="submit-btn">{t.seCta}</Link>
      </div>

      {shotIndex !== null && createPortal(
        <div
          className="wa-lightbox"
          onClick={() => setShotIndex(null)}
          role="dialog"
          aria-modal="true"
        >
          <button className="wa-lightbox-close" onClick={() => setShotIndex(null)} aria-label={t.close || 'X'}>
            <X size={28} />
          </button>
          <img
            src={testimonialShots[shotIndex].src}
            alt={t.waProofAlt}
            className="wa-lightbox-img"
            onClick={(e) => e.stopPropagation()}
          />
        </div>,
        document.body
      )}
    </div>
  );
}
